"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowRight, Calendar, Clock } from "lucide-react";
const articles = [{
  id: 1,
  title: "The Art of Capturing Natural Light in Golden Hour",
  excerpt: "A deep dive into how we utilize the softest light of the day to create those signature airy and romantic wedding portraits.",
  category: "Artistry",
  date: "Nov 12, 2023",
  readTime: "5 min read",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675594/img/golden_hour_photography.png"
}, {
  id: 2,
  title: "Curating Your Heirloom Wedding Album",
  excerpt: "Discover our process for selecting the most emotionally resonant images to tell your unique story in a physical collection.",
  category: "Experience",
  date: "Nov 08, 2023",
  readTime: "7 min read",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675594/img/heirloom_album_curation.png"
}, {
  id: 3,
  title: "Ivory Lens Featured in Modern Bride Magazine",
  excerpt: "We are honored to share our latest editorial feature highlighting contemporary wedding trends and timeless aesthetics.",
  category: "Journal",
  date: "Nov 02, 2023",
  readTime: "3 min read",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675594/img/magazine_feature_ivory.png"
}, {
  id: 4,
  title: "Mastering the Soft-Toned Aesthetic",
  excerpt: "Why a desaturated and refined color palette ensures your wedding photographs remain timeless for decades to come.",
  category: "Artistry",
  date: "Oct 25, 2023",
  readTime: "6 min read",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675594/img/soft_toned_aesthetic.png"
}, {
  id: 5,
  title: "Planning Your Engagement Session",
  excerpt: "Tips for choosing a location and wardrobe that reflects your personality while maintaining a sophisticated visual story.",
  category: "Experience",
  date: "Oct 18, 2023",
  readTime: "8 min read",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675594/img/engagement_session_planning.png"
}, {
  id: 6,
  title: "Winter Weddings: A Study in Cool Tones",
  excerpt: "Exploring the unique beauty of winter light and how we capture the crisp, elegant atmosphere of cold-weather celebrations.",
  category: "Journal",
  date: "Oct 10, 2023",
  readTime: "4 min read",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675594/img/winter_wedding_tones.png"
}];
const categories = ["Artistry", "Experience", "Journal"];
export default function HealthcareKnowledgeHub() {
  return <section data-section-id="226479" id="lets-connect" className="bg-gradient-to-b from-background to-secondary/50 py-24">
      <div className="container mx-auto px-4 md:px-6">
        
        {}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h1 className="font-default text-4xl md:text-6xl font-light text-foreground mb-6 tracking-tight">
            Tell Us Your Story
          </h1>
          <p className="font-sans text-lg md:text-xl text-foreground/70 font-light leading-relaxed">
            Explore our journal of timeless moments, artistic insights, and the refined experience of Ivory Lens photography.
          </p>
        </div>

        {}
        <Tabs defaultValue="Artistry" className="w-full max-w-6xl mx-auto">
          <div className="flex justify-center mb-12">
            <TabsList className="h-auto p-1.5 rounded-md bg-background/40 backdrop-blur-sm border border-border/30 shadow-sm overflow-x-auto whitespace-nowrap md:overflow-visible md:whitespace-normal max-w-full">
              {categories.map((category, index) => <TabsTrigger key={category} value={category} data-index={index} className="rounded-md px-4 py-2 md:px-6 md:py-2.5 font-sans text-sm tracking-wider uppercase data-[state=active]:bg-background data-[state=active]:shadow-md">
                  {category}
                </TabsTrigger>)}
            </TabsList>
          </div>

          {categories.map((category, categoryIndex) => <TabsContent key={category} value={category} data-index={categoryIndex} className="mt-0 outline-none focus-visible:ring-0">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {articles.filter(article => article.category === category).map((article, articleIndex) => <div key={article.id} data-index={articleIndex} className="group bg-card rounded-md overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 border border-border/20 flex flex-col">
                      <div className="aspect-[16/10] bg-muted relative overflow-hidden">
                        <img src={article.image} alt={article.title} data-wvc-srcset={`${article.image}-wvc-srcset`} data-wvc-sizes={`${article.image}-wvc-sizes`} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 opacity-90 group-hover:opacity-100" />
                      </div>
                      <div className="p-8 flex flex-col flex-grow">
                        <div className="flex items-center gap-4 text-xs text-foreground/50 mb-4 font-sans tracking-widest uppercase">
                          <span className="flex items-center gap-1.5">
                            <SafeHydrate><Calendar className="size-3 text-accent-foreground/40" /></SafeHydrate> 
                            {article.date}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Clock className="size-3 text-accent-foreground/40" /> 
                            {article.readTime}
                          </span>
                        </div>
                        <h3 className="font-default text-2xl font-normal text-foreground mb-3 line-clamp-2 group-hover:text-accent-foreground/80 transition-colors">
                          {article.title}
                        </h3>
                        <p className="font-sans text-foreground/60 font-light mb-8 line-clamp-3 flex-grow leading-relaxed">
                          {article.excerpt}
                        </p>
                        <Button variant="link" className="p-0 h-auto justify-start text-foreground font-sans font-normal text-sm tracking-widest uppercase group/btn" asChild>
                          <Link to="/CollectionsPage">
                            View Story 
                            <ArrowRight className="size-3 ml-2 transition-transform group-hover/btn:translate-x-1" />
                          </Link>
                        </Button>
                      </div>
                    </div>)}
              </div>
            </TabsContent>)}
        </Tabs>

      </div>
    </section>;
}